import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DatePicker, Input, Table } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import css from "./DishesStatisticsComponent.module.css";
import { ImageReusable } from "../../../ui";
import { formatDateToString } from "../../../helpers/formatDateToString";
import { statisticsService } from "../../../services/statistics-services";
import { OrdersInfoSingleDishModal } from "../../ordersSection/ordersSectionModals/OrdersInfoSingleDishModal/OrdersInfoSingleDishModal";

const { RangePicker } = DatePicker;
const { Search } = Input;

const SEARCH_DEBOUNCE_MS = 400;

const todayDate = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const getLocalizedDishName = (name, lang) => {
  if (!name || typeof name !== "object") {
    return "—";
  }
  const code = typeof lang === "string" ? lang.split("-")[0] : "en";
  const fromLocale = name[code] ?? name[lang];
  if (fromLocale) return fromLocale;
  if (name.en) return name.en;
  const first = Object.values(name).find((v) => typeof v === "string" && v.trim());
  return first || "—";
};

const DishesStatisticsComponent = () => {
  const { i18n } = useTranslation();
  const defaultDay = useMemo(todayDate, []);

  const [startDate, setStartDate] = useState(() => new Date(defaultDay));
  const [endDate, setEndDate] = useState(() => new Date(defaultDay));

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selectedDishId, setSelectedDishId] = useState(null);
  const [isShowSingleDish, setIsShowSingleDish] = useState(false);

  const skipDebounceOnMount = useRef(true);
  useEffect(() => {
    if (skipDebounceOnMount.current) {
      skipDebounceOnMount.current = false;
      return;
    }
    const id = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  const previousDebouncedRef = useRef(debouncedSearch);
  useEffect(() => {
    if (previousDebouncedRef.current === debouncedSearch) {
      return;
    }
    previousDebouncedRef.current = debouncedSearch;
    setPage(1);
  }, [debouncedSearch]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await statisticsService.getDishesCountChart({
        startDate: formatDateToString(startDate),
        endDate: formatDateToString(endDate),
        page: String(page),
        limit: String(Math.min(pageSize, 100)),
        search: debouncedSearch || undefined,
      });
      setData(Array.isArray(res?.dishes) ? res.dishes : []);
      setTotal(res?.pagination?.total ?? 0);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load data");
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, page, pageSize, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onRangeChange = (dates) => {
    if (!dates?.[0] || !dates?.[1]) {
      return;
    }
    const s = dates[0].toDate();
    const e = dates[1].toDate();
    if (s > e) {
      setError("Start date cannot be after end date");
      return;
    }
    setError("");
    setStartDate(s);
    setEndDate(e);
    setPage(1);
  };

  const onSearchChange = (e) => {
    setSearchInput(e?.target?.value ?? "");
  };

  const onPaginationChange = (nextPage, nextSize) => {
    if (nextSize && nextSize !== pageSize) {
      setPageSize(nextSize);
      setPage(1);
    } else {
      setPage(nextPage);
    }
  };

  const handleDishRowClick = (record) => {
    if (!record?.dishId) {
      return;
    }
    setSelectedDishId(record.dishId);
    setIsShowSingleDish(true);
  };

  const closeSingleDishModal = (open) => {
    setIsShowSingleDish(open);
    if (open === false) {
      setSelectedDishId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Image",
        dataIndex: "img",
        key: "img",
        width: 80,
        align: "center",
        render: (img, record) => (
          <div className={css.image}>
            <ImageReusable
              src={typeof img === "string" ? img : undefined}
              className={css.imageTag}
              alt={getLocalizedDishName(record?.name, i18n.language)}
            />
          </div>
        ),
      },
      {
        title: "Dish name",
        dataIndex: "name",
        key: "name",
        ellipsis: true,
        render: (name) => getLocalizedDishName(name, i18n.language),
      },
      {
        title: "Count",
        dataIndex: "count",
        key: "count",
        width: 120,
        align: "right",
        render: (c) => (typeof c === "number" ? c : "—"),
      },
    ],
    [i18n.language]
  );

  const rangeValue = [dayjs(startDate), dayjs(endDate)];

  return (
    <section className={css.wrap}>
      <div className={css.controlsCard}>
        <div className={css.controlGroup}>
          <span className={css.label}>Date range</span>
          <RangePicker
            className={css.rangePicker}
            value={rangeValue}
            onChange={onRangeChange}
            format="YYYY-MM-DD"
            maxDate={dayjs()}
            allowClear={false}
          />
        </div>
        <Search
          className={css.search}
          placeholder="Search by dish name"
          allowClear
          value={searchInput}
          onChange={onSearchChange}
        />
      </div>

      {error && !loading && <div className={css.error}>{error}</div>}

      <div className={css.tableContainer}>
        <Table
          rowKey="dishId"
          dataSource={data}
          columns={columns}
          loading={loading}
          size="small"
          scroll={{ x: "max-content" }}
          pagination={{
            current: page,
            pageSize: Math.min(pageSize, 100),
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (totalRows) => `Total ${totalRows} dish(es)`,
            onChange: onPaginationChange,
          }}
          locale={{ emptyText: "No data" }}
          onRow={(record) => ({
            onClick: () => handleDishRowClick(record),
            style: { cursor: "pointer" },
          })}
        />
      </div>

      {isShowSingleDish && selectedDishId && (
        <OrdersInfoSingleDishModal
          setIsShowSingleDish={closeSingleDishModal}
          selectedDishId={selectedDishId}
        />
      )}
    </section>
  );
};

export { DishesStatisticsComponent };
